/**
 * 선택 툴바 (2026-08-21) — 워크스페이스의 그것과 같은 자리·같은 모양.
 *
 * 사용자 지적: "선택되면 위치를 워크스페이스의 선택바처럼 **헤드에 올려서** 쓰는 게 좋겠다."
 *
 * ★ 목록 머리줄을 **대체한다**(그 아래에 끼우지 않는다). 끼우면 고르는 순간 목록이 한 줄
 *   밀려, 끌어서 고르던 손이 다른 줄을 칠한다 — 띄우는 방식으로 피해 놨던 그 문제가
 *   자리만 바꿔 되돌아온다.
 * ★ 순서도 워크스페이스와 같다: 해제(✕) → 개수 → 할 일. 두 화면에서 같은 손이 같은 자리를 찾는다.
 */

import { X, MailOpen, Star, Trash2, Shield, FolderInput, RotateCcw } from '../../icons';
import { tMail } from '../../lib/mail-i18n';

interface MailSelectionBarProps {
  count: number;
  busy: boolean;
  /**
   * 스팸함 — **복원 · 휴지통 둘뿐**이다 (2026-08-26 사용자 지시).
   * 스팸으로 치워 둔 편지에 읽음·중요·이동은 뜻이 없다. 되돌리거나, 아예 버리거나다.
   */
  inSpam: boolean;
  /**
   * 휴지통에서는 **복원·영구삭제 둘뿐이다** (2026-08-25 사용자 지시).
   *
   * 여기서 읽음·중요·스팸은 뜻이 없다 — 버린 편지에 별을 달거나 읽음으로 만들 이유가 없고,
   * '휴지통' 단추는 이미 휴지통에 있는 것을 또 휴지통에 넣는 모양이라 손이 멈춘다.
   */
  inTrash: boolean;
  /** 휴지통에서만 온다 — 고른 것을 영원히 지운다. 옛 서버면 안 온다(단추도 안 그린다). */
  onPurge?: () => void;
  onClear: () => void;
  onRead: () => void;
  onStar: () => void;
  onSpam: () => void;
  onNotSpam: () => void;
  onTrash: () => void;
  /** 옮길 메일함이 있을 때만 온다. */
  onMove?: () => void;
}

export function MailSelectionBar(props: MailSelectionBarProps) {
  const { count, busy, inSpam, inTrash, onClear, onRead, onStar, onSpam, onNotSpam, onTrash, onMove, onPurge } = props;
  return (
    <div className="mail-selbar" role="toolbar" aria-label={tMail('mail.bulk.aria_bar')}>
      <button
        type="button"
        className="mail-selbar-clear"
        onClick={onClear}
        aria-label={tMail('mail.bulk.clear')}
      >
        <X size={16} aria-hidden />
      </button>
      <span className="mail-selbar-count">
        {tMail('mail.bulk.picked').replace('{n}', String(count))}
      </span>

      {/* 스팸함 — 되돌리거나 휴지통으로. 그 밖의 일은 여기서 뜻이 없다(2026-08-26 사용자 지시). */}
      {inSpam ? (
        <>
          <button type="button" className="mail-selbar-btn" disabled={busy} onClick={onNotSpam}>
            <RotateCcw size={14} aria-hidden />
            {tMail('mail.bulk.restore')}
          </button>
          <button
            type="button"
            className="mail-selbar-btn mail-selbar-btn--danger"
            disabled={busy}
            onClick={onTrash}
          >
            <Trash2 size={14} aria-hidden />
            {tMail('mail.bulk.trash')}
          </button>
        </>
      ) : /* 휴지통 — 되돌리거나 영영 지우거나. */ inTrash ? (
        <>
          <button type="button" className="mail-selbar-btn" disabled={busy} onClick={onTrash}>
            <RotateCcw size={14} aria-hidden />
            {tMail('mail.bulk.restore')}
          </button>
          {onPurge && (
            <button
              type="button"
              className="mail-selbar-btn mail-selbar-btn--danger"
              disabled={busy}
              onClick={onPurge}
            >
              <Trash2 size={14} aria-hidden />
              {tMail('mail.bulk.purge')}
            </button>
          )}
        </>
      ) : (
      <>
      <button type="button" className="mail-selbar-btn" disabled={busy} onClick={onRead}>
        <MailOpen size={14} aria-hidden />
        {tMail('mail.bulk.read')}
      </button>
      <button type="button" className="mail-selbar-btn" disabled={busy} onClick={onStar}>
        <Star size={14} aria-hidden />
        {tMail('mail.bulk.star')}
      </button>
      {/* 여기까지 온 것은 스팸함도 휴지통도 아니다 — 되돌리기(스팸 아님)는 위에서 끝났다. */}
      <button type="button" className="mail-selbar-btn" disabled={busy} onClick={onSpam}>
        <Shield size={14} aria-hidden />
        {tMail('mail.bulk.spam')}
      </button>
      {onMove && (
        <button type="button" className="mail-selbar-btn" disabled={busy} onClick={onMove}>
          <FolderInput size={14} aria-hidden />
          {tMail('mail.row.move')}
        </button>
      )}
      <button
        type="button"
        className="mail-selbar-btn mail-selbar-btn--danger"
        disabled={busy}
        onClick={onTrash}
      >
        <Trash2 size={14} aria-hidden />
        {tMail('mail.bulk.trash')}
      </button>
      </>
      )}
    </div>
  );
}
